import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Building2, AlertTriangle, Loader2 } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { portfolioAPI } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';

/**
 * One client from the portfolio view.
 *
 * This page is why the Risk Dashboard appeared to log people out. Clicking a client row did
 * `window.location.href = '/app/portfolio/client/<id>'`, and no such route existed — so the
 * router's catch-all sent the browser to `/`, and because a full page load rebooted the app the
 * user landed on the logged-out marketing page with their session still perfectly valid.
 *
 * The backend endpoint (`GET /api/portfolio/clients/{id}`) had been there the whole time; only the
 * route and this page were missing.
 *
 * `risk_level` may be absent. An imported client carries identity only until something genuinely
 * assesses it, so "not assessed" is rendered as its own state rather than collapsing into the
 * lowest risk band.
 */
const RISK_VARIANT = { high: 'destructive', elevated: 'warning', normal: 'success' };

export default function PortfolioClientDetail() {
    const { clientId } = useParams();
    const navigate = useNavigate();
    const [client, setClient] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await portfolioAPI.client(clientId);
                if (alive) setClient(data);
            } catch (e) {
                if (alive) setError(e?.response?.status === 404 ? 'notfound' : 'error');
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [clientId]);

    const back = (
        <Button variant="ghost" size="sm" onClick={() => navigate('/app/portfolio')}
                data-testid="client-detail-back">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Risk Dashboard
        </Button>
    );

    if (loading) {
        return (
            <div className="p-8" data-testid="client-detail-loading">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-8 space-y-4" data-testid="client-detail-error">
                {back}
                <div className="bg-card border border-border rounded-xl p-10 text-center">
                    <AlertTriangle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                    <h2 className="text-lg font-medium">
                        {error === 'notfound' ? 'Client not found' : 'Could not load this client'}
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        {error === 'notfound'
                            ? 'This client is not in the portfolio view.'
                            : 'Please try again.'}
                    </p>
                </div>
            </div>
        );
    }

    const exceptions = client.exceptions || [];
    const transactions = client.recent_transactions || [];
    const assessed = Boolean(client.risk_level);

    return (
        <div className="p-8 space-y-6 max-w-5xl" data-testid="client-detail">
            {back}

            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
                        <Building2 className="h-6 w-6 text-muted-foreground shrink-0" />
                        <span className="truncate">{client.company_name || client.name || 'Client'}</span>
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        CVR {client.cvr_number || '—'}
                        {client.assigned_staff ? ` · ${client.assigned_staff}` : ''}
                    </p>
                </div>
                {assessed ? (
                    <Badge variant={RISK_VARIANT[client.risk_level] || 'outline'}
                           data-testid="client-detail-risk">
                        {client.risk_level} · {client.risk_score}
                    </Badge>
                ) : (
                    <Badge variant="outline" data-testid="client-detail-risk">Not assessed</Badge>
                )}
            </div>

            {!assessed && (
                <p className="text-xs text-muted-foreground" data-testid="client-detail-unassessed">
                    No risk score has been computed for this client. Imported clients carry their
                    identity only until something genuinely assesses them.
                </p>
            )}

            <section>
                <h2 className="text-sm font-medium mb-2">
                    Exceptions ({exceptions.length})
                </h2>
                {exceptions.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="client-detail-no-exceptions">
                        No exceptions recorded for this client.
                    </p>
                ) : (
                    <ul className="space-y-1" data-testid="client-detail-exceptions">
                        {exceptions.map((e) => (
                            <li key={e.id} className="flex items-center gap-2 text-sm border border-border rounded px-3 py-2">
                                <Badge variant={e.severity === 'high' ? 'destructive' : 'outline'}
                                       className="text-xs">{e.severity}</Badge>
                                <span className="flex-1 truncate">{e.description || e.type}</span>
                                <span className="text-xs text-muted-foreground">{e.status}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section>
                <h2 className="text-sm font-medium mb-2">
                    Recent transactions ({transactions.length})
                </h2>
                {transactions.length === 0 ? (
                    <p className="text-sm text-muted-foreground" data-testid="client-detail-no-transactions">
                        No transactions recorded for this client.
                    </p>
                ) : (
                    <ul className="space-y-1 font-mono text-xs" data-testid="client-detail-transactions">
                        {transactions.slice(0, 25).map((tx, i) => (
                            <li key={tx.id || i} className="flex items-center gap-3 border-b border-border/50 py-1">
                                <span className="w-24 text-muted-foreground">{String(tx.date || '').slice(0, 10)}</span>
                                <span className="flex-1 truncate">{tx.description || tx.text || '—'}</span>
                                <span>{formatCurrency(Number(tx.amount || 0), 'DKK')}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    );
}
